import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageTitle from "../../components/common/PageTitle";
import FullPageLoader from "../../components/ui/FullPageLoader";
import {
  API_URL,
  APP_CURRENCY,
  DEFAULT_PER_PAGE,
  OCTOREWARDS_CURRENCY_ID,
} from "../../utilities/constants";
import Pagination from "../../components/common/Pagination";
import ButtonTextIcon from "../../components/ui/ButtonTextIcon";
import ButtonLightTextIcon from "../../components/ui/ButtonLightTextIcon";
import {
  CiCircleChevRight,
  CiCircleCheck,
  CiMoneyBill,
  CiFileOn,
  CiFilter,
  CiUser,
  CiCircleRemove,
} from "react-icons/ci";
import ResultsPerPage from "../_common/ResultsPerPage";
import RSModal from "../../components/ui/RSModal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { formatAmountValue, formatDateTime } from "../../utilities/helpers";
import { useUI } from "../../context/ui";
import { MdOutlineAccountCircle } from "react-icons/md";

// Helper function to build query parameters
const buildQueryParams = (params) =>
  Object.entries(params)
    .filter(
      ([_, value]) => value !== "" && value !== undefined && value !== null
    )
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

// Helper function to format filters as readable string
const formatFiltersAsReadableString = (filters) =>
  Object.entries(filters)
    .filter(
      ([_, value]) => value !== "" && value !== undefined && value !== null
    )
    .map(([key, value]) => {
      let readableKey;
      switch (key) {
        case "transactionType":
          readableKey = "Type";
          break;
        case "transactionCategory":
          readableKey = "Category";
          break;
        case "fromDate":
          readableKey = "From Date";
          break;
        case "toDate":
          readableKey = "To Date";
          break;
        default:
          readableKey =
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1");
      }
      return readableKey;
    })
    .join(", ");

// FieldTitle component
const FieldTitle = ({ title }) => (
  <p
    className="title f-w-700 color-primary"
    style={{ marginBottom: "5px", fontSize: "14px" }}
  >
    {title}
  </p>
);

// FilterContent component for modal
const FilterContent = ({ setShow, initialValues, pageSize }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const validationSchema = Yup.object({
    fromDate: Yup.string()
      .nullable()
      .test(
        "is-not-future-date",
        "From date cannot be in the future",
        function (value) {
          if (!value) return true;
          const selectedDate = new Date(value);
          const today = new Date();
          return selectedDate <= today;
        }
      )
      .test(
        "is-before-toDate",
        "From date cannot be after To date",
        function (value) {
          const { toDate } = this.parent;
          if (!value || !toDate) return true;
          const fromDate = new Date(value);
          const endDate = new Date(toDate);
          return fromDate <= endDate;
        }
      ),
    toDate: Yup.string()
      .nullable()
      .test(
        "is-not-future-date",
        "To date cannot be in the future",
        function (value) {
          if (!value) return true;
          const selectedDate = new Date(value);
          const today = new Date();
          return selectedDate <= today;
        }
      )
      .test(
        "is-after-fromDate",
        "To date cannot be before From date",
        function (value) {
          const { fromDate } = this.parent;
          if (!value || !fromDate) return true;
          const endDate = new Date(value);
          const startDate = new Date(fromDate);
          return endDate >= startDate;
        }
      ),
    transactionType: Yup.string(),
    transactionCategory: Yup.string(),
  });

  return (
    <div className="pos-rel" style={{ width: "480px", padding: "40px" }}>
      <CiCircleRemove
        className="cursor-pointer transition hover-color-primary"
        onClick={() => setShow(false)}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          fontSize: "32px",
        }}
      />
      <h4 className="color-primary">Filter Transactions</h4>
      <br />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          const queryParams = buildQueryParams({
            pageNo: 0,
            pageSize,
            transactionType: values.transactionType,
            transactionCategory: values.transactionCategory,
            fromDate: values.fromDate,
            toDate: values.toDate,
            currencyId: initialValues.currencyId,
            sortOrder: initialValues.sortOrder,
          });
          navigate(`${location.pathname}?${queryParams}`);
          setShow(false);
        }}
      >
        {({ errors, touched, resetForm, setFieldValue }) => (
          <Form>
            <div className="flex center-center" style={{ gap: "10px" }}>
              <div style={{ width: "100%" }}>
                <FieldTitle title="From Date" />
                <Field
                  name="fromDate"
                  type="date"
                  className={`col-1-1 ${
                    errors.fromDate && touched.fromDate ? "error" : ""
                  }`}
                />
                <ErrorMessage
                  name="fromDate"
                  component="p"
                  className="error-messages"
                />
              </div>
              <div style={{ width: "100%" }}>
                <FieldTitle title="To Date" />
                <Field
                  name="toDate"
                  type="date"
                  className={`col-1-1 ${
                    errors.toDate && touched.toDate ? "error" : ""
                  }`}
                />
                <ErrorMessage
                  name="toDate"
                  component="p"
                  className="error-messages"
                />
              </div>
            </div>
            <br />
            <div className="flex center-center" style={{ gap: "10px" }}>
              <div style={{ width: "100%" }}>
                <FieldTitle title="Transaction Type" />
                <Field
                  name="transactionType"
                  as="select"
                  className={`col-1-1 ${
                    errors.transactionType && touched.transactionType
                      ? "error"
                      : ""
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="LOYALTY_CREDIT">Credit</option>
                  <option value="LOYALTY_DEBIT">Debit</option>
                </Field>
              </div>
              <div style={{ width: "100%" }}>
                <FieldTitle title="Transaction Category" />
                <Field
                  name="transactionCategory"
                  as="select"
                  className={`col-1-1 ${
                    errors.transactionCategory && touched.transactionCategory
                      ? "error"
                      : ""
                  }`}
                >
                  <option value="">All Categories</option>
                  <option value="BONUS">Bonus</option>
                  <option value="CLAIM">Claim</option>
                  <option value="CONVERT">Convert</option>
                  <option value="BASE">Base</option>
                </Field>
              </div>
            </div>
            <br />
            <div className="flex center-left" style={{ gap: "10px" }}>
              <ButtonLightTextIcon
                type="button"
                title="Reset"
                onClick={() => {
                  resetForm();
                  setShow(false);
                  navigate(
                    `${location.pathname}?pageNo=0&pageSize=${pageSize}&currencyId=${initialValues.currencyId}&sortOrder=${initialValues.sortOrder}`
                  );
                }}
                icon={<CiCircleRemove />}
              />
              <ButtonTextIcon
                type="submit"
                title="Search"
                icon={<CiCircleCheck />}
                className="responsive-button"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

// TransactionCard component for table rows
const TransactionCard = ({ trx, index }) => {
  const isCredit = trx.transactionType.includes("CREDIT");
  const formattedDateTime = formatDateTime(trx.createdAt);

  const getCategoryInfo = () => {
    switch (trx.transactionCategory) {
      case "BONUS":
        return {
          description: `Bonus Reward Earned - ${trx.transactionRefNumber}`,
        };
      case "CLAIM":
        return { description: `Reward Claimed - ${trx.transactionRefNumber}` };
      case "CONVERT":
        return {
          description: `Reward Converted - ${trx.transactionRefNumber}`,
        };
      default:
        return {
          description: `Base Reward Earned - ${trx.transactionRefNumber}`,
        };
    }
  };

  const categoryInfo = getCategoryInfo();

  return (
    <tr style={{ backgroundColor: isCredit ? "#EAF8F4" : "#FFEBEB" }}>
      <td>{index + 1}</td>
      <td>{trx.transactionRefNumber || "-"}</td>
      <td>{trx.transactionCategory || "-"}</td>
      <td className="f-w-700">{isCredit ? "Credit" : "Debit"}</td>
      <td>
        {APP_CURRENCY} {formatAmountValue(trx.amount)}/-
      </td>
      <td>{formatAmountValue(trx.postBalance)}</td>
      <td>{formattedDateTime}</td>
      <td>{categoryInfo.description}</td>
    </tr>
  );
};

const Rewards = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [transactions, setTransactions] = useState({
    transactions: [],
    totalPages: 0,
    totalRecords: 0,
  });
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const { setSegmentTitle } = useUI();
  const searchParams = new URLSearchParams(location.search);
  const pageNo = parseInt(searchParams.get("pageNo") || 0);
  const pageSize = parseInt(searchParams.get("pageSize") || DEFAULT_PER_PAGE);
  const transactionType = searchParams.get("transactionType") || "";
  const transactionCategory = searchParams.get("transactionCategory") || "";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";
  const currencyId = searchParams.get("currencyId") || OCTOREWARDS_CURRENCY_ID; // Default currencyId
  const sortOrder = searchParams.get("sortOrder") || "DESC";

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const resp = await fetch(
        `${API_URL}/gasolina/api/1.0.0/octorewards/clients/dashboard`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );
      if (resp.ok) {
        const response = await resp.json();
        setDashboardData(response.data);
      }
    } catch (e) {
      console.error("Error fetching dashboard data", e);
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  // Fetch client transactions
  const fetchClientTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const queryParams = buildQueryParams({
        ...(currencyId && { currencyId }),
        ...(sortOrder && { sortOrder }),
        ...(pageNo !== undefined && { pageNo }), // allow 0 as valid
        ...(pageSize !== undefined && { pageSize }), // allow 0 as valid
        ...(transactionType && { transactionType }),
        ...(transactionCategory && { transactionCategory }),
        ...(fromDate && { startDate: fromDate + "T00:00:00" }),
        ...(toDate && { endDate: toDate + "T23:59:59" }),
      });

      const resp = await fetch(
        `${API_URL}/gasolina/api/1.0.0/octorewards/clients/transactions?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );
      if (resp.ok) {
        const response = await resp.json();
        setTransactions({
          transactions: response.data.transactions || [],
          totalPages: response.data.totalPages || 0,
          totalRecords: response.data.totalRecords || 0,
        });
      }
    } catch (e) {
      console.error("Error fetching client transactions", e);
    } finally {
      setLoadingTransactions(false);
    }
  }, [
    currencyId,
    sortOrder,
    pageNo,
    pageSize,
    transactionType,
    transactionCategory,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    setSegmentTitle("Rewards Dashboard");
    fetchDashboardData();
    fetchClientTransactions();
  }, [fetchDashboardData, fetchClientTransactions]);

  return (
    <section className="pos-rel">
      <PageTitle title="Rewards Dashboard" />
      <FullPageLoader isActive={loadingDashboard || loadingTransactions} />

      {dashboardData && (
        <div className="dashboard">
          {/* Dashboard Cards */}
          {dashboardData.currencies.map((currency) => (
            <div
              key={currency.currencyId}
              style={{
                background: "#fff",
                borderRadius: "8px",
                padding: "0 20px",
              }}
            >
              <div
                className="metrics-grid"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                {[
                  {
                    label: "Total Transactions",
                    value: `${currency.totalTransactions || 0} Transactions`,
                    icon: (
                      <CiFileOn
                        className="color-secondary"
                        style={{ height: "40px", width: "auto" }}
                      />
                    ),
                  },
                  {
                    label: "Total Credit Transactions",
                    value: `${
                      currency.totalCreditTransactions || 0
                    } Transactions`,
                  },
                  {
                    label: "Total Credit Amount",
                    value: `${APP_CURRENCY} ${formatAmountValue(
                      currency.totalCreditAmount
                    )}/-`,
                  },
                  {
                    label: "Total Debit Transactions",
                    value: `${
                      currency.totalDebitTransactions || 0
                    } Transactions`,
                  },
                  {
                    label: "Total Debit Amount",
                    value: `${APP_CURRENCY} ${formatAmountValue(
                      currency.totalDebitAmount
                    )}/-`,
                  },
                  {
                    label: "Total Base Amount",
                    value: `${APP_CURRENCY} ${formatAmountValue(
                      currency.totalBaseAmount
                    )}/-`,
                  },
                  {
                    label: "Total Bonus Amount",
                    value: `${APP_CURRENCY} ${formatAmountValue(
                      currency.totalBonusAmount
                    )}/-`,
                  },
                  {
                    label: "Total Claim Amount",
                    value: `${APP_CURRENCY} ${formatAmountValue(
                      currency.totalClaimAmount
                    )}/-`,
                  },
                  {
                    label: "Total Convert Amount",
                    value: `${APP_CURRENCY} ${formatAmountValue(
                      currency.totalConvertAmount
                    )}/-`,
                  },
                  {
                    label: "Total Customers",
                    value: `${dashboardData.totalCustomers || 0} Customers`,
                    icon: <CiUser size={40} />,
                  },
                ].map((metric, index) => (
                  <div
                    key={index}
                    style={{
                      flex: "1 1 300px",
                      minWidth: "200px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      padding: "12px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      background: "#fafafa",
                    }}
                  >
                    {metric.icon || (
                      <CiMoneyBill
                        className="color-secondary"
                        style={{ height: "40px", width: "auto" }}
                      />
                    )}
                    <div>
                      <p className="color-faded font-size-12 f-w-400">
                        {metric.label}
                      </p>
                      <h6 className="color-primary f-w-700">{metric.value}</h6>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Filters and Results Per Page */}
          <div
            className="flex center-left responsive-container"
            style={{ marginTop: "20px" }}
          >
            <ResultsPerPage
              targetURL={`${location.pathname}?${buildQueryParams({
                transactionType,
                transactionCategory,
                fromDate,
                toDate,
                currencyId,
                sortOrder,
              })}&pageNo=0&pageSize=`}
            />
            <div
              className="flex center-right"
              style={{
                gap: "10px",
                marginLeft: "auto",
              }}
            >
              <RSModal
                selector={
                  <ButtonTextIcon
                    type="button"
                    title="Filter"
                    icon={<CiFilter />}
                  />
                }
                content={
                  <FilterContent
                    setShow={(val) => val}
                    initialValues={{
                      transactionType,
                      transactionCategory,
                      fromDate,
                      toDate,
                      currencyId,
                      sortOrder,
                    }}
                    pageSize={pageSize}
                  />
                }
              />
              <ButtonTextIcon
                type="button"
                title="View All Customers"
                icon={<CiCircleChevRight />}
                className="responsive-button"
                onClick={() => navigate("/rewards/customers")}
              />
            </div>
          </div>

          {/* Transactions Table */}
          <div className="results">
            <div className="rs-table-holder">
              <table className="rs-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Transaction Ref</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Balance</th>
                    <th>Date</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {!loadingTransactions && transactions.transactions.length > 0
                    ? transactions.transactions.map((trx, i) => (
                        <TransactionCard
                          key={trx.transactionRefNumber}
                          trx={trx}
                          index={pageNo * pageSize + i}
                        />
                      ))
                    : !loadingTransactions && (
                        <tr>
                          <td
                            colSpan={8}
                            style={{ textAlign: "center", padding: "10px" }}
                          >
                            <p>No transactions found</p>
                          </td>
                        </tr>
                      )}
                </tbody>
              </table>
              {loadingTransactions && (
                <div className="flex center-center" style={{ padding: "20px" }}>
                  <p>Loading transactions...</p>
                </div>
              )}
            </div>
            {transactions.totalRecords > 0 && (
              <Pagination
                totalRecords={transactions.totalRecords}
                pageNo={pageNo}
                pageSize={pageSize}
                baseUrl={`${location.pathname}?${buildQueryParams({
                  transactionType,
                  transactionCategory,
                  fromDate,
                  toDate,
                  currencyId,
                  sortOrder,
                })}&`}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Rewards;
